<template>
    <v-card
        class="ide-card"
        height="550"
        color="surface"
        elevation="0">
        <div class="ide">
            <!-- Window controls and one tab per snippet. -->
            <div class="ide__titlebar">
                <div class="ide__dots">
                    <span
                        v-for="dot in 3"
                        :key="dot" />
                </div>
                <div
                    ref="tabs"
                    class="ide__tabs">
                    <div
                        v-for="(tab, i) in files"
                        :key="tab.path"
                        class="ide__tab"
                        :class="{ 'ide__tab--active': i === index }">
                        <v-icon
                            x-small
                            :color="tab.language.color">
                            {{ tab.language.icon }}
                        </v-icon>
                        {{ tab.name }}
                        <v-icon
                            class="ide__close"
                            x-small>
                            mdi-close
                        </v-icon>
                    </div>
                </div>
            </div>

            <div class="ide__breadcrumbs">
                <span
                    v-for="(part, i) in crumbs"
                    :key="i"
                    class="ide__crumb">
                    {{ part }}
                </span>
            </div>

            <div class="ide__editor">
                <div
                    class="ide__lines"
                    :style="{ '--line': cursor.line - 1 }">
                    <div class="ide__current-line" />
                    <div class="ide__gutter">
                        <div
                            v-for="n in cursor.line"
                            :key="n"
                            :class="{ 'ide__gutter--active': n === cursor.line }">
                            {{ n }}
                        </div>
                    </div>
                    <div
                        class="ide__code"
                        v-html="html" />
                </div>
            </div>

            <div class="ide__status">
                <span>
                    <v-icon x-small>mdi-source-branch</v-icon>
                    main
                </span>
                <span>
                    <v-icon x-small>mdi-close-circle-outline</v-icon>
                    0
                    <v-icon x-small>mdi-alert-outline</v-icon>
                    0
                </span>
                <v-spacer />
                <span>Ln {{ cursor.line }}, Col {{ cursor.column }}</span>
                <span class="d-none d-lg-inline">Spaces: 4</span>
                <span class="d-none d-lg-inline">UTF-8</span>
                <span>{{ file.language.name }}</span>
                <v-icon x-small>mdi-bell-outline</v-icon>
            </div>
        </div>
    </v-card>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { codeSnippets } from '@/data/codeSnippets';
import { useTypewriter } from '@/composables/useTypewriter';
import { highlight } from '@/utils/highlight';

// Tab icon, icon color and status bar label for each file extension.
const LANGUAGES = {
    ts: { name: 'TypeScript', icon: 'mdi-language-typescript', color: '#3178c6' },
    php: { name: 'PHP', icon: 'mdi-language-php', color: '#8892bf' },
    py: { name: 'Python', icon: 'mdi-language-python', color: '#ffd43b' },
    js: { name: 'JavaScript', icon: 'mdi-language-javascript', color: '#f7df1e' },
};

const files = codeSnippets.map(({ path }) => ({
    path,
    name: path.split('/').pop(),
    language: LANGUAGES[path.split('.').pop()],
}));

/** Wraps each leading 4-space indent level in a span drawn as an indent guide. */
const withIndentGuides = (html) =>
    html.replace(/^(?: {4})+/gm, (indent) => indent.replace(/ {4}/g, '<span class="indent">    </span>'));

/** Fake IDE that types, highlights and erases the code snippets in a loop. */
export default {
    name: 'CodeScreen',
    setup() {
        const { text, index } = useTypewriter(
            codeSnippets.map((snippet) => snippet.code),
            { typeDelay: 50, eraseDelay: 1, holdDelay: 1000, jitter: true },
        );
        const tabs = ref(null);
        const file = computed(() => files[index.value]);

        const cursor = computed(() => {
            const lines = text.value.split('\n');
            return { line: lines.length, column: lines[lines.length - 1].length + 1 };
        });

        // Keeps the active tab centered when the tabs overflow the title bar.
        watch(index, (i) => {
            const strip = tabs.value;
            const tab = strip?.children[i];
            if (tab) strip.scrollTo({ left: tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
        });

        return {
            files,
            file,
            index,
            tabs,
            cursor,
            crumbs: computed(() => file.value.path.split('/')),
            html: computed(() => `${withIndentGuides(highlight(text.value))}<span class="cursor ide__caret"></span>`),
        };
    },
};
</script>

<style scoped>
.ide-card {
    overflow: hidden;
}
.ide {
    display: flex;
    flex-direction: column;
    height: 100%;
    filter: url(#noise) url(#turbulence);
}

.ide__titlebar {
    display: flex;
    flex-shrink: 0;
    height: 35px;
    background: var(--v-surface-base);
}
.ide__dots {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 14px;
}
.ide__dots span {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #ff5f57;
}
.ide__dots span:nth-child(2) {
    background: #febc2e;
}
.ide__dots span:nth-child(3) {
    background: #28c840;
}
.ide__tabs {
    position: relative;
    display: flex;
    overflow-x: auto;
    scrollbar-width: none;
}
.ide__tabs::-webkit-scrollbar {
    display: none;
}
.ide__tab {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 6px;
    padding: 0 10px 0 12px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    border-top: 2px solid transparent;
    border-right: 1px solid rgba(0, 0, 0, 0.25);
    transition: color 0.2s, background 0.2s;
}
.ide__tab--active {
    color: #fff;
    background: var(--v-active-tab-base);
    border-top-color: var(--v-accent-base);
}
.ide__close {
    visibility: hidden;
}
.ide__tab--active .ide__close {
    visibility: visible;
}

.ide__breadcrumbs {
    flex-shrink: 0;
    height: 22px;
    padding: 0 14px;
    font-size: 12px;
    line-height: 22px;
    color: rgba(255, 255, 255, 0.5);
    white-space: nowrap;
    overflow: hidden;
}
.ide__crumb + .ide__crumb::before {
    content: '›';
    margin: 0 6px;
}
.ide__crumb:last-child {
    color: rgba(255, 255, 255, 0.8);
}

.ide__editor {
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
}
.ide__lines {
    --line-height: 19px;
    --padding-top: 4px;
    position: relative;
    display: flex;
    width: max-content;
    min-width: 100%;
    min-height: 100%;
    padding-top: var(--padding-top);
    font-family: ui-monospace, 'Cascadia Mono', 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace !important;
    font-size: 14px;
    font-weight: 400;
    line-height: var(--line-height);
}
.ide__current-line {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(var(--padding-top) + var(--line) * var(--line-height));
    height: var(--line-height);
    background: rgba(255, 255, 255, 0.05);
}
.ide__gutter {
    position: sticky;
    left: 0;
    z-index: 1;
    padding: 0 16px 0 12px;
    min-width: 36px;
    font-size: 11px;
    text-align: right;
    color: rgba(255, 255, 255, 0.3);
    background: var(--v-surface-base);
    user-select: none;
}
.ide__gutter--active {
    color: rgba(255, 255, 255, 0.85);
}
.ide__code {
    position: relative;
    padding-right: 12px;
    white-space: pre;
}
.ide__code ::v-deep .indent {
    background: linear-gradient(to right, rgba(255, 255, 255, 0.16) 1px, transparent 1px);
}
.ide__code ::v-deep .ide__caret {
    display: inline-block;
    width: 2px;
    height: var(--line-height);
    margin-right: -2px;
    vertical-align: top;
    background: var(--v-accent-base);
}

.ide__status {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 12px;
    height: 22px;
    padding: 0 10px;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    background: var(--v-primary-base);
}
.ide__status .v-icon {
    color: inherit;
}
</style>
